// The phone apps' bridge (window.moodsNative + their MoodsNative plugin through Capacitor's injected `window.Capacitor`)
// without any app imports, so low-level modules (store/auth, lib/api via desktop-bridge) can use it. Everything else
// goes through lib/native.ts. The iOS app (MoodsNativePlugin.swift) and the Android app (MoodsNativePlugin.kt) answer
// the same methods with the same events. In a browser and in the desktop app `nativeApp` is null and every helper here
// is a no-op.
import {
  NATIVE_PLUGIN,
  type MoodsNativeShell,
  type NativePushRegistration,
  type NativePushStatus,
  type NativeSecret,
  type NativeVoiceStateEvent,
} from '@moods/shared';

const shell = typeof window !== 'undefined' ? window.moodsNative : undefined;

/** The phone app this web build runs in (iOS or Android), or null. */
export const nativeApp: MoodsNativeShell | null = shell && (shell.platform === 'ios' || shell.platform === 'android') ? shell : null;

/** One call to the app's plugin; rejects outside the phone apps. */
export function nativeCall<T = void>(method: string, options: object = {}): Promise<T> {
  const cap = typeof window !== 'undefined' ? window.Capacitor : undefined;
  if (!nativeApp || !cap) return Promise.reject(new Error('not in a MOODS phone app'));
  return cap.nativePromise<T>(NATIVE_PLUGIN, method, options);
}

/** Listens to an event of the app's plugin (no-op outside the phone apps). */
export function onNativeEvent(event: string, fn: (data: unknown) => void): () => void {
  const sub = nativeApp && typeof window !== 'undefined' ? window.Capacitor?.addListener(NATIVE_PLUGIN, event, fn) : undefined;
  return () => void sub?.remove();
}

/** A secure-storage item of the app — the Keychain, the Android Keystore (null when missing or the call fails). */
export function getNativeSecret(key: NativeSecret): Promise<string | null> {
  return nativeCall<{ value?: string | null }>('getSecret', { key })
    .then(r => r.value ?? null)
    .catch(() => null);
}

/** Writes (or with null deletes) a secure-storage item of the app. */
export function setNativeSecret(key: NativeSecret, value: string | null): Promise<void> {
  return nativeCall('setSecret', { key, value }).catch(() => undefined);
}

/** The app's notification permission (`prompt` when never asked or outside the app). */
export function nativePushStatus(): Promise<NativePushStatus> {
  return nativeCall<{ status?: NativePushStatus }>('pushStatus')
    .then(r => r.status ?? 'prompt')
    .catch(() => 'prompt' as const);
}

/** Asks for permission when never asked (from a tap), then the device token (APNs / FCM). Rejects when there is none. */
export function nativePushRegister(): Promise<NativePushRegistration> {
  return nativeCall<NativePushRegistration>('pushRegister');
}

/**
 * The native voice engine (1.1: MoodsVoiceEngine.swift / .kt): the call's audio — microphone, voices, stream sound —
 * over a native LiveKit connection that mixes with other apps and goes on behind the lock screen.
 * rtc/VoiceController.ts › nativeVoice drives it.
 */
export const nativeVoice = {
  connect: (o: { url: string; token: string; e2eeKey: string | null; publish: boolean; microphone: boolean; deafened: boolean }) => nativeCall('voiceConnect', o),
  disconnect: () => nativeCall('voiceDisconnect').catch(() => undefined),
  setMicrophone: (enabled: boolean) => nativeCall('voiceSetMicrophone', { enabled }),
  setDeafened: (deafened: boolean) => nativeCall('voiceSetDeafened', { deafened }),
  setVolumes: (v: { voice: Record<string, number>; stream: Record<string, number> }) => nativeCall('voiceSetVolumes', v),
  onState: (fn: (e: NativeVoiceStateEvent) => void): (() => void) => onNativeEvent('voiceState', data => fn(data as NativeVoiceStateEvent)),
};
